import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { useQuery } from "react-query";
import { fieldMappingFunctions, fieldTypes, StdFieldTypes } from "./allData";
import CustomSelect from "@components/common/CustomSelect";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Input } from "@components/ui/input";
import PageLoader from "@components/loaders/PageLoader";
import { toast } from "@components/ui/use-toast";
import { fetchLogMappingFields } from "./createPluginApi";
// import LogMappingSaveOptionsDialog from "./LogMaappingSaveOptionsDialog";
import ParquetFields from "./ParquetFields";
import InfoTooltip from "@components/common/InfoTooltip";

interface FieldMappingDialogProps {
  open: boolean;
  onClose: () => void;
  responseData: Record<string, any> | null;
  selectedFieldPath?: string | null;
  // onSaveMapping: any;
  setLogMappedFields?: React.Dispatch<React.SetStateAction<any>>;
  setMappingSaveData?: React.Dispatch<React.SetStateAction<any[]>>;
  currentBundleId?: any;
  requestId?: number | undefined;
  selectedOption?: string | null;
  pluginId: string;
}
const columnTypeOptions = [
  { label: "TEXT", value: "TEXT" },
  { label: "INTEGER", value: "INTEGER" },
];

const dataTypeOptions = [
  { label: "hash", value: "hash" },
  { label: "ip", value: "ip" },
  { label: "domain", value: "domain" },
  { label: "url", value: "url" },
  { label: "file", value: "file" },
  { label: "email", value: "email" },
  { label: "mac", value: "mac" },
  { label: "string", value: "string" },
  { label: "number", value: "number" },
];

const FieldMappingDialog: React.FC<FieldMappingDialogProps> = ({
  open,
  onClose,
  responseData,
  selectedFieldPath,
  setMappingSaveData,
  currentBundleId,
  requestId,
  selectedOption,
  pluginId,
}) => {
  const [allFields, setAllFields] = React.useState<StdFieldTypes>([]);
  const [selectedFieldTypes, setSelectedFieldTypes] = React.useState<
    Record<string, string>
  >({});

  const [customFieldNames, setCustomFieldNames] = React.useState<
    Record<string, string>
  >({});
  const [selectedFunctions] = React.useState<Record<string, string>>({});
  const [createNewField] = React.useState<Record<string, boolean>>({});
  const [customFieldTypes, setCustomFieldTypes] = React.useState<
    Record<string, string>
  >({});
  const [customCategories, setCustomCategories] = React.useState<
    Record<string, string>
  >({});
  const [functionValues] = React.useState<Record<string, string>>({});
  // TODO : need in the future
  const [openParquetFieldsDialog, setOpenParquetFieldsDialog] =
    React.useState(false);
  const [columnTypes, setColumnTypes] = React.useState<Record<string, string>>(
    {},
  );
  const [dataTypes, setDataTypes] = React.useState<Record<string, string>>({});

  const [selectedFieldDisplayNames, setSelectedFieldDisplayNames] =
    React.useState<Record<string, string>>({});
  const [selectedFieldCategories, setSelectedFieldCategories] = React.useState<
    Record<string, string>
  >({});
  const [timeFieldKey, setTimeFieldKey] = React.useState<string | null>(null);
  // const [saveLogType, setSaveLogType] = React.useState<'Datalist' | 'Parquet' | ''>('');

  // Fetch log mapping fields
  const { data: logmappingdata, isLoading } = useQuery(
    ["fetchLogMappingFields"],
    ({ signal }) => fetchLogMappingFields(signal),
  );

  // Populate allFields from logmappingdata
  React.useEffect(() => {
    if (logmappingdata?.data?.standard) {
      const temp = Object.entries(logmappingdata.data.standard).map(
        ([key, value]: [string, any], index: number) => ({
          standard: key,
          value: key,
          display_name: value?.display_name ?? "",
          label: key,
          ...(typeof value === "object" && value !== null ? value : {}),
          id: index + 1,
        }),
      );
      setAllFields(temp);
    }
  }, [logmappingdata]);

  // Helper function to extract array indices from selectedFieldPath
  const extractArrayIndices = (path: string): Record<string, number> => {
    const indices: Record<string, number> = {};
    if (!path) return indices;

    // Match patterns like "entityAnomalies[2]" or "fields[1]"
    const matches = path.matchAll(/([a-zA-Z_][a-zA-Z0-9_]*)\[(\d+)\]/g);
    let currentPath = "";

    for (const match of matches) {
      const [, arrayName, index] = match;
      currentPath = currentPath ? `${currentPath}.${arrayName}` : arrayName;
      indices[currentPath] = parseInt(index, 10);
    }

    return indices;
  };

  // Improved handling for responseData to cover more cases
  const mappedFields = React.useMemo(() => {
    // Handle single field selection (primitive value with path)
    if (selectedFieldPath && typeof responseData !== "object") {
      // Extract the field name from the path (e.g., "data.entityAnomalies[0].anomaly_type" -> "anomaly_type")
      const fieldName = selectedFieldPath.split(".").pop() || selectedFieldPath;
      return {
        [fieldName]: String(responseData),
      };
    }

    // Handle array selection (e.g. clicking "[" on item.vehicles)
    // Keep arrays as one flattened string under the selected path.
    if (selectedFieldPath && Array.isArray(responseData)) {
      return {
        [selectedFieldPath]: JSON.stringify(responseData),
      };
    }

    // Handle object/array or no path provided
    if (!responseData || typeof responseData !== "object") return {};

    // Extract array indices from selectedFieldPath to use correct indices
    const arrayIndices = selectedFieldPath
      ? extractArrayIndices(selectedFieldPath)
      : {};
    return flattenObject(responseData, "", {}, arrayIndices);
  }, [responseData, selectedFieldPath]);

  // Utility function to flatten nested objects
  interface FlattenedObject {
    [key: string]: string;
  }

  // Replace the flattenObject function around line 120

  function flattenObject(
    obj: any,
    parentKey = "",
    result: FlattenedObject = {},
    arrayIndices: Record<string, number> = {},
  ): FlattenedObject {
    // Handle arrays at the root or nested level as a single flattened string.
    if (Array.isArray(obj)) {
      result[parentKey || "root"] = JSON.stringify(obj);
      return result;
    }

    // Handle objects
    if (typeof obj === "object" && obj !== null) {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;

        // If it's an array, keep it as a single flattened string field.
        if (Array.isArray(value)) {
          result[fullKey] = JSON.stringify(value);
        } else if (typeof value === "object" && value !== null) {
          // Recurse into nested objects
          flattenObject(value, fullKey, result, arrayIndices);
        } else {
          // Primitive value
          result[fullKey] = String(value);
        }
      });

      return result;
    }

    // Handle primitives
    result[parentKey || "root"] = String(obj);
    return result;
  }

  // Filter out the field types that are already selected
  const getAvailableFieldTypes = (currentKey: string) => {
    const currentSelected = selectedFieldTypes[currentKey];
    return allFields.filter(
      (field) =>
        !Object.entries(selectedFieldTypes).some(
          ([k, v]) => k !== currentKey && v === field.value,
        ) || field.value === currentSelected,
    );
  };

  const fieldDisplayNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    allFields.forEach((f) => {
      map[f.value] = f.display_name ?? "";
    });
    return map;
  }, [allFields]);

  const fieldCategoryMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    allFields.forEach((f) => {
      map[f.value] = f.category ?? "";
    });
    return map;
  }, [allFields]);

  // Handle changes in field type, function, custom field name, and create new field toggle
  const handleFieldTypeChange = (fieldKey: string, selectedValue: string) => {
    setSelectedFieldTypes((prev) => ({
      ...prev,
      [fieldKey]: selectedValue,
    }));

    setSelectedFieldDisplayNames((prev) => ({
      ...prev,
      [fieldKey]: fieldDisplayNameMap[selectedValue] || "",
    }));
    setSelectedFieldCategories((prev) => ({
      ...prev,
      [fieldKey]: fieldCategoryMap[selectedValue] || "",
    }));
  };

  // Handle function selection
  // const handleFunctionChange = (fieldKey: string, func: string) => {
  //   setSelectedFunctions((prev) => ({
  //     ...prev,
  //     [fieldKey]: func,
  //   }));

  //   const selectedFunc = fieldMappingFunctions.find((f) => f.value === func);
  //   if (!selectedFunc?.isInput) {
  //     setFunctionValues((prev) => {
  //       const updated = { ...prev };
  //       delete updated[fieldKey];
  //       return updated;
  //     });
  //   }
  // };

  // Handle custom field name input
  const handleCustomFieldChange = (fieldKey: string, value: string) => {
    setCustomFieldNames((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  // Handle toggle for creating a new field (needed in the future)
  // const handleCreateNewToggle = (fieldKey: string, checked: boolean) => {
  //   setCreateNewField((prev) => ({
  //     ...prev,
  //     [fieldKey]: checked,
  //   }));

  //   // Clear data on toggle for safety
  //   if (checked) {
  //     setSelectedFieldTypes((prev) => {
  //       const updated = { ...prev };
  //       delete updated[fieldKey];
  //       return updated;
  //     });
  //   } else {
  //     setCustomFieldNames((prev) => {
  //       const updated = { ...prev };
  //       delete updated[fieldKey];
  //       return updated;
  //     });
  //   }
  // };

  // Handle custom field type and category changes
  const handleCustomTypeChange = (fieldKey: string, value: string) => {
    setCustomFieldTypes((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  // Handle custom category change
  const handleCustomCategoryChange = (fieldKey: string, value: string) => {
    setCustomCategories((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  // Generate the final mapping based on user selections
  const generateFinalMapping = () => {
    let fieldIdCounter = 1;
    const finalMapping: any[] = [];

    Object.entries(mappedFields).forEach(([key]) => {
      const isCreatingNew = createNewField[key];
      const fieldType = selectedFieldTypes[key];
      const customField = customFieldNames[key];
      const functionName = selectedFunctions[key];

      // Only include if either standard mapping or custom field is provided
      if (
        (isCreatingNew && customField?.trim()) ||
        (!isCreatingNew && fieldType)
      ) {
        // Construct the full path for rawFieldName
        let fullPath = key;
        if (selectedFieldPath) {
          // Check if this is a single field selection (selectedFieldPath ends with the key)
          const pathEndsWithKey =
            selectedFieldPath.endsWith(`.${key}`) ||
            selectedFieldPath.endsWith(key);
          if (pathEndsWithKey) {
            // Single field selection: use the full path as-is
            fullPath = selectedFieldPath;
          } else {
            // Multiple fields from object: append the key to create the full path
            fullPath = `${selectedFieldPath}.${key}`;
          }
        }

        const row: any = {
          fieldId: fieldIdCounter++,
          rawFieldName: fullPath,
          type: "string",
          display_name: selectedFieldDisplayNames[key] || "",
          is_time: timeFieldKey === key,
          // isDatalistType: isDatalistMap[key] ?? false,
        };
        if (selectedOption === "saveDataList") {
          row.column_type = columnTypes[key] || "";
          row.data_type = dataTypes[key] || "";
          row.display_name = selectedFieldDisplayNames[key] || "";
        }

        if (isCreatingNew && customField?.trim()) {
          row.display_name = customField.trim();
          row.category = customCategories[key] || "string";
          row.type = customFieldTypes[key] || "string";
        } else if (fieldType) {
          row.mappingLsField = fieldType;
          row.display_name = selectedFieldDisplayNames[key] || "";
          row.category = selectedFieldCategories[key] || "";
        }

        // Only add function_name if selected
        if (functionName) {
          row.function_name = functionName;
          if (
            fieldMappingFunctions.find((f) => f.value === functionName)?.isInput
          ) {
            row.function_value = functionValues[key] || "";
          }
        }

        finalMapping.push(row);
      }
    });
    return finalMapping;
  };

  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined)
      return <span className="text-gray-400 italic">null</span>;

    // For arrays or objects, stringify them directly
    if (Array.isArray(value)) {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return <span>{String(value)}</span>;
    }

    if (Array.isArray(value)) {
      return (
        <ul className="pl-4 list-disc space-y-1">
          {value.map((item, index) => (
            <li key={index}>{renderValue(item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === "object") {
      return (
        <div className="pl-2 border-l border-gray-300 ml-2 space-y-1">
          {Object.entries(value).map(([key, val], idx) => (
            <div key={idx}>
              <strong>{key}:</strong> {renderValue(val)}
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  // Handle save action
  const handleSaveFields = (data: any) => {
    const result = generateFinalMapping();
    if (result.length === 0) {
      toast({
        title: "No valid mappings found",
        description:
          "Please ensure you have selected at least one field to map.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setMappingSaveData?.((prev) => {
      const bundleId = currentBundleId;
      const bundleMappings = prev[bundleId] || [];
      return {
        ...prev,
        [bundleId]: [
          ...bundleMappings,
          {
            bundleId,
            logUid: data.logUid || "",
            logTitile: data.logTitle || "",
            logType: data.logType || "",
            logCategory: data.logCategory || "",
            log_ls_product_type: data.lsProductType || [],
            logCronFrequency: data.cronFrequency || 0,
            mappingFields: result,
            requestId: requestId || null,
            cronType: data.cronType || "",
            dataListName: data.dataListName || "",
            typeDatalist: data.typeDatalist || "",
            display_name: data.display_name || "",
          },
        ],
      };
    });

    // Reset time field selection
    setTimeFieldKey(null);
    onClose();
  };

  const handleSaveLogFields = (type: string) => {
    if (type === "Parquet") {
      setOpenParquetFieldsDialog(true);
    }
  };

  // handle parquet save
  const getParquetFieldsData = (data: any) => {
    handleSaveFields(data);
  };

  const validateSaveDataList = () => {
    Object.keys(mappedFields).forEach((key) => {
      const hasFieldType = !!selectedFieldTypes[key];
      const hasColumnType = !!columnTypes[key];
      const hasDataType = !!dataTypes[key];

      const filledCount = [hasFieldType, hasColumnType, hasDataType].filter(
        Boolean,
      ).length;

      // untouched row → skip validation
      if (filledCount === 0) return;

      // partially filled row → error
      if (filledCount < 3) {
        toast({
          title: "Validation failed",
          description: `Please complete all fields for "${key}"`,
          variant: "destructive",
          duration: 3000,
        });
        throw new Error("validation_failed");
      }
    });

    return true;
  };

  const getDropdownOptions = (currentKey: string) => {
    const excludedValues = [
      "Time",
      "TimeEpoch",
      "time",
      "timeepoch",
      "EpochTime",
      "epochtime",
      "Epoch Time",
      "epoch time",
      "time_epoch",
      "epoch_time",
    ];
    return getAvailableFieldTypes(currentKey)
      .filter((field) => !excludedValues.includes(field.value))
      .map((field) => ({
        ...field,
        label: field.display_name || field.label || field.value,
      }));
  };

  return (
    <>
      {/*TODO :  needed in the future   */}
      {/* {openLogMappingOptions && (
        <LogMappingSaveOptionsDialog
          open={openLogMappingOptions}
          handleClose={() => setOpenLogMappingOptions(false)}
          handleSaveLogFields={handleSaveLogFields}
        />
      )} */}

      {openParquetFieldsDialog && (
        <ParquetFields
          open={openParquetFieldsDialog}
          handleClose={() => setOpenParquetFieldsDialog(false)}
          getParquetFieldsData={getParquetFieldsData}
          currentBundleId={currentBundleId}
          selectedOption={selectedOption}
          pluginId={pluginId}
        />
      )}

      <PageLoader show={isLoading} />
      <Dialog open={open} onOpenChange={onClose ?? (() => {})} modal={false}>
        <div
          className={
            "w-full h-full bg-modalBackground top-0 left-0 z-50 " +
            (open ? "fixed" : "hidden")
          }
        ></div>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className="mt-5 max-h-[85vh] max-w-[90%] overflow-y-auto"
        >
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Field Mapping</DialogTitle>
          </DialogHeader>
          <div className="border border-secondary rounded-md">
            <Table>
              <TableHeader className="text-lg font-semibold">
                <TableRow>
                  <TableHead>Fields</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Mapped Field</TableHead>
                  {selectedOption === "saveDataList" && (
                    <>
                      <TableHead>Column Type</TableHead>
                      <TableHead>Data Type</TableHead>
                      {/* <TableHead>Is Data List</TableHead> */}
                    </>
                  )}
                  {selectedOption !== "saveDataList" && (
                    <TableHead className="flex items-center gap-2">
                      Is Time
                      <InfoTooltip text="Select the field that represents as the time field. Only one field can be selected as time." />
                    </TableHead>
                  )}
                  {/* <TableHead>Functions</TableHead> */}
                  {/* <TableHead>Custom Field Type</TableHead> */}
                  {/* <TableHead>New Field Name</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(mappedFields).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell>{key}</TableCell>

                    <TableCell className="break-all max-w-[300px]">
                      {typeof value === "object" && value !== null
                        ? renderValue(value)
                        : String(value)}
                    </TableCell>

                    <TableCell>
                      {!createNewField[key] && (
                        <CustomSelect
                          data={getDropdownOptions(key)}
                          placeholder="Please select type"
                          defaultValue={
                            selectedFieldTypes[key]
                              ? [selectedFieldTypes[key]]
                              : []
                          }
                          sorted={true}
                          onSelect={(selected: string) =>
                            handleFieldTypeChange(key, selected)
                          }
                        />
                      )}
                    </TableCell>
                    {selectedOption === "saveDataList" && (
                      <>
                        {/* Column Type (dropdown) */}
                        <TableCell>
                          <CustomSelect
                            data={columnTypeOptions}
                            placeholder="Select column type"
                            defaultValue={
                              columnTypes[key] ? [columnTypes[key]] : []
                            }
                            onSelect={(selected?: string) => {
                              setColumnTypes((prev) => {
                                const updated = { ...prev };
                                if (!selected) {
                                  delete updated[key];
                                  return updated;
                                }
                                updated[key] = selected;
                                return updated;
                              });
                            }}
                          />
                        </TableCell>

                        {/* Data Type */}
                        <TableCell>
                          <CustomSelect
                            data={dataTypeOptions}
                            placeholder="Select data type"
                            defaultValue={
                              dataTypes[key] ? [dataTypes[key]] : []
                            }
                            onSelect={(selected?: string) => {
                              setDataTypes((prev) => {
                                const updated = { ...prev };

                                if (!selected) {
                                  delete updated[key];
                                  return updated;
                                }

                                updated[key] = selected;
                                return updated;
                              });
                            }}
                          />
                        </TableCell>

                        {/* Is Data List */}
                        {/* <TableCell>
                          <CustomSelect
                            data={getAvailableIsDatalist(key)}
                            placeholder="Select"
                            defaultValue={
                              key in isDatalistMap ? [String(isDatalistMap[key])] : []
                            }
                            onSelect={(selected?: string) => {
                              setIsDatalistMap((prev) => {
                                const updated: Record<string, boolean> = { ...prev };


                                if (!selected) {
                                  delete updated[key];
                                  return updated;
                                }


                                if (selected === "true") {
                                  Object.keys(updated).forEach((k) => {
                                    updated[k] = false;
                                  });
                                  updated[key] = true;
                                  return updated;
                                }


                                updated[key] = false;
                                return updated;
                              });
                            }}
                          />
                        </TableCell> */}
                      </>
                    )}

                    {/* Is Time Checkbox */}
                    {selectedOption !== "saveDataList" && (
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={timeFieldKey === key}
                          disabled={!selectedFieldTypes[key]}
                          onChange={(e) => {
                            if (!selectedFieldTypes[key]) return;

                            if (e.target.checked) {
                              setTimeFieldKey(key);
                            } else {
                              setTimeFieldKey(null);
                            }
                          }}
                          className={`cursor-pointer w-4 h-4 ${
                            !selectedFieldTypes[key]
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          title={
                            !selectedFieldTypes[key]
                              ? "Map a field first"
                              : "Time field"
                          }
                        />
                      </TableCell>
                    )}

                    {/* <TableCell>
                      <Select
                        onValueChange={(val) => handleFunctionChange(key, val)}
                        value={selectedFunctions[key] || ""}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Function" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {fieldMappingFunctions.map((func) => (
                              <SelectItem key={func.value} value={func.value}>
                                {func.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>

                      {selectedFunctions[key] &&
                        fieldMappingFunctions.find((f) => f.value === selectedFunctions[key])
                          ?.isInput && (
                          <Input
                            type="text"
                            placeholder="Enter function value"
                            value={functionValues[key] || ""}
                            onChange={(e) => {
                              setFunctionValues((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }));
                            }}
                            className="mt-2"
                          />
                        )}
                    </TableCell> */}

                    {/* <TableCell>
                      <input
                        type="checkbox"
                        checked={!!createNewField[key]}
                        onChange={(e) => handleCreateNewToggle(key, e.target.checked)}
                      />
                    </TableCell> */}

                    <TableCell>
                      {createNewField[key] && (
                        <div className="flex flex-col gap-1">
                          <Input
                            type="text"
                            placeholder="Enter new field name"
                            className="border p-2 rounded w-full"
                            value={customFieldNames[key] || ""}
                            onChange={(e) =>
                              handleCustomFieldChange(key, e.target.value)
                            }
                          />

                          <Select
                            value={customFieldTypes[key] || ""}
                            onValueChange={(val) =>
                              handleCustomTypeChange(key, val)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {fieldTypes.map((field) => (
                                  <SelectItem key={field} value={field}>
                                    {field}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>

                          <Input
                            type="text"
                            placeholder="Enter category"
                            className="border p-2 rounded w-full"
                            value={customCategories[key] || ""}
                            onChange={(e) =>
                              handleCustomCategoryChange(key, e.target.value)
                            }
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                if (selectedOption === "saveDataList") {
                  const isValid = validateSaveDataList();
                  if (!isValid) return;
                }

                handleSaveLogFields("Parquet");
              }}
            >
              Save fields
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FieldMappingDialog;
